import { Form, FormikProvider, useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";
import * as Yup from "yup";

import {
  INFERENCE_STATUSES,
  InferenceStatus,
  LayerAlias,
  UserPolices
} from "constant";
import features from "features";
import { spendTime } from "helpers/spendTime";
import usePolicy from "hooks/usePolicy";
import { useDispatch } from "react-redux";
import { RootStateInterface } from "reducer";
import { LayersManagerStateInterface } from "scenes/LayersManager/interfaces";

import CommonButton from "components/buttons/CommonButton/CommonButton";
import IconButton from "components/buttons/IconButton/IconButton";
import PrimaryButton from "components/buttons/PrimaryButton/PrimaryButton";
import ShowHideButton from "components/buttons/ShowHideButton/ShowHideButton";
import InferenceAction from "components/InferenceAction/InferenceAction";
import InferenceStatusComponent from "components/InferenceStatusComponent/InferenceStatusComponent";
import FormField from "components/inputs/FormField/FormField";
import ModalWrapper from "components/modals/ModalWrapper/ModalWrapper";

import DownloadIcon from "assets/icons/download.svg";
import DragHandleDotsIcon from "assets/icons/drag-handle-dots.svg";
import EyeCloseGrayIcon from "assets/icons/eye-close-gray.svg";
import EyeOpenGrayIcon from "assets/icons/eye-open-gray.svg";
import MarkersFlagOff from "assets/icons/markers-flag-off.svg";
import MarkersFlagOn from "assets/icons/markers-flag.svg";
import TrashIcon from "assets/icons/trash.svg";

import styles from "./provider-manager-modal.module.scss";

export interface ProviderManagerModalInterface {
  title?: string;
  description?: string;
  isDisableClose?: boolean;
  acceptButtonLabel?: string;
  acceptButtonAction?: () => void;
  isWithProgress?: boolean;
  id: string;
  alias: LayerAlias;
  initProviders: any;
  onHide: (updatedProviders: any) => void;
}

const ProviderManagerModal = () => {
  const dispatch = useDispatch();
  const { hasPolicy } = usePolicy();

  const {
    title,
    description,
    isDisableClose,
    alias,
    id,
    initProviders,
    onHide
  } = useSelector<RootStateInterface, ProviderManagerModalInterface>(
    (state) => state.modal.modalProps
  );

  const { providers, isCreateProviderLoading } = useSelector<
    RootStateInterface,
    LayersManagerStateInterface
  >((state) => state.layersManager);

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const { destination, draggableId: providerId } = result;
    const index = destination.index;

    dispatch(
      features.layersManager.actions.reorderProviderRequest({
        params: { id, alias, providerId },
        fields: { index }
      })
    );
  };

  const initialValues = {
    link: ""
  };

  const validationSchema = Yup.object().shape({
    link: Yup.string().required("Odm link is required")
  });

  const onSubmit = useCallback(
    (e) => {
      dispatch(
        features.layersManager.actions.createProviderRequest({
          params: { id, alias },
          fields: { ...e }
        })
      );
      formik.resetForm();
    },
    [alias, dispatch, id]
  );

  const onDelete = useCallback(
    (providerId) => () => {
      dispatch(
        features.layersManager.actions.deleteProviderRequest({
          params: { id, alias, providerId }
        })
      );
    },
    [alias, dispatch, id]
  );

  const formik = useFormik({
    validateOnChange: true,
    initialValues,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    onSubmit
  });

  useEffect(() => {
    dispatch(
      features.layersManager.actions.setProviders({
        params: { alias, id },
        providers: initProviders
      })
    );
  }, [alias, dispatch, id, initProviders]);

  const durationTime = useCallback((inference) => {
    if (!inference) return "";

    const { status, data } = inference;

    if (
      status === InferenceStatus.PROCESSING ||
      status === InferenceStatus.DONE
    )
      return spendTime({
        startTime: data.stats?.inference_time_start_at,
        endTime: data.stats?.inference_time_end_at
      });
    else if (
      status === InferenceStatus.PREPARING ||
      status === InferenceStatus.PREPARED
    ) {
      return spendTime({
        startTime: data.stats?.upload_time_start_at,
        endTime: data.stats?.upload_time_end_at
      });
    }
  }, []);

  const onUpdateVisibilityProvidersMarkers = useCallback(
    (provider) => () => {
      dispatch(
        features.layersManager.actions.setProvidersMarkersIsActive({
          providerId: provider.id,
          isActiveMarkers: !provider?.isActiveMarkers,
          alias
        })
      );
    },
    [dispatch, alias]
  );

  const isAtLeastOneVisibleProvidersMarkers = useMemo(
    () => providers.some((item) => item.isActiveMarkers),
    [providers]
  );

  const onUpdateVisibilityAllProvidersMarkers = useCallback(() => {
    dispatch(
      features.layersManager.actions.setAllProvidersMarkersIsActive({
        alias,
        isActiveMarkers: !isAtLeastOneVisibleProvidersMarkers
      })
    );
  }, [dispatch, alias, isAtLeastOneVisibleProvidersMarkers]);

  const onUpdateVisibilityProvidersTiles = useCallback(
    (provider) => () => {
      dispatch(
        features.layersManager.actions.setProvidersTilesIsActive({
          providerId: provider.id,
          isActiveTiles: !provider?.isActiveTiles,
          alias
        })
      );
    },
    [dispatch, alias]
  );

  const isAtLeastOneVisibleProvidersTiles = useMemo(
    () => providers.some((item) => item.isActiveTiles),
    [providers]
  );

  const onUpdateVisibilityAllProvidersTiles = useCallback(() => {
    dispatch(
      features.layersManager.actions.setAllProvidersTilesIsActive({
        alias,
        isActiveTiles: !isAtLeastOneVisibleProvidersTiles
      })
    );
  }, [dispatch, isAtLeastOneVisibleProvidersTiles, alias]);

  useEffect(
    () => () => {
      dispatch(features.layersManager.actions.clearProvidersState(alias));
    },
    []
  );

  const onClose = useCallback(() => {
    onHide(providers);
    dispatch(features.modal.actions.hideModal());
    dispatch(
      features.layersManager.actions.setLayerIsActive({
        params: { alias },
        fields: {
          isActive:
            isAtLeastOneVisibleProvidersTiles ||
            isAtLeastOneVisibleProvidersMarkers
        }
      })
    );
    dispatch(features.layersManager.actions.clearProvidersState(alias));
  }, [
    dispatch,
    onHide,
    providers,
    isAtLeastOneVisibleProvidersTiles,
    isAtLeastOneVisibleProvidersMarkers,
    alias
  ]);

  return (
    <ModalWrapper
      className={styles["modal-container"]}
      isDisableClose={isDisableClose}
      onHide={onClose}
    >
      <div className={styles["modal"]}>
        <div className={styles["modal__text-container"]}>
          <h2 className={styles["modal__title"]}>{title}</h2>
          <h4 className={styles["modal__description"]}>
            {description ?? "Manage files"}
          </h4>
          <div className={styles["modal__providers-table"]}>
            <div className={styles["providers-table__head"]}>
              <div className={styles["providers-table__head-controls"]}>
                <ShowHideButton
                  onClick={onUpdateVisibilityAllProvidersTiles}
                  isActive={isAtLeastOneVisibleProvidersTiles}
                  enabledIcon={EyeOpenGrayIcon}
                  disabledIcon={EyeCloseGrayIcon}
                />
                <ShowHideButton
                  onClick={onUpdateVisibilityAllProvidersMarkers}
                  isActive={isAtLeastOneVisibleProvidersMarkers}
                  enabledIcon={MarkersFlagOn}
                  disabledIcon={MarkersFlagOff}
                />
              </div>
              <div>Name</div>
              <div>ODM link</div>
              <div>Action</div>
              <div>Inference action</div>
              <div>Time</div>
              <div>Inference status</div>
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {providers?.map((item, index) => (
                      <Draggable
                        key={item.id}
                        draggableId={item.id.toString()}
                        index={index}
                        isDragDisabled={
                          !hasPolicy(UserPolices.PROJECT_PROVIDER_REORDER)
                        }
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              ...provided.draggableProps.style
                            }}
                            className={styles["providers-table__body"]}
                          >
                            <div className={styles["drag-dots"]}>
                              <img src={DragHandleDotsIcon} alt="drag-dots" />
                              <ShowHideButton
                                onClick={onUpdateVisibilityProvidersTiles(item)}
                                isActive={item.isActiveTiles}
                                enabledIcon={EyeOpenGrayIcon}
                                disabledIcon={EyeCloseGrayIcon}
                              />
                              <ShowHideButton
                                onClick={onUpdateVisibilityProvidersMarkers(
                                  item
                                )}
                                isActive={item.isActiveMarkers}
                                enabledIcon={MarkersFlagOn}
                                disabledIcon={MarkersFlagOff}
                              />
                            </div>
                            <div className={styles["name"]}>
                              {item.data.name}
                            </div>
                            <div className={styles["link"]}>
                              {item.data.link}
                            </div>
                            {hasPolicy(UserPolices.PROJECT_PROVIDER_DELETE) && (
                              <div>
                                <IconButton
                                  onClick={onDelete(item.id)}
                                  icon={TrashIcon}
                                  alt="trash-icon"
                                  disabled={item.isDeleteProviderLoading}
                                  isLoading={item.isDeleteProviderLoading}
                                />
                              </div>
                            )}
                            <InferenceAction
                              id={id}
                              providerId={item.id}
                              alias={alias}
                              status={
                                INFERENCE_STATUSES[item?.inference?.status]
                              }
                              isLoading={item.isInferenceStatusWaiting}
                              disabled={item.isDeleteProviderLoading}
                            />
                            <div className={styles["name"]}>
                              {durationTime(item?.inference)}
                            </div>
                            <InferenceStatusComponent
                              status={
                                INFERENCE_STATUSES[item?.inference?.status]
                              }
                              completionPercentage={
                                item?.inference?.status ===
                                InferenceStatus.PROCESSING
                                  ? item?.inference?.data?.completion_percentage
                                  : undefined
                              }
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {hasPolicy(UserPolices.PROJECT_PROVIDER_CREATE) && (
              <FormikProvider value={formik}>
                <Form className={styles["form"]}>
                  <FormField
                    className={styles["field"]}
                    placeholder="Add link"
                    name={"link"}
                  />
                  <CommonButton
                    type="submit"
                    icon={DownloadIcon}
                    isLoading={isCreateProviderLoading}
                    disabled={isCreateProviderLoading}
                  >
                    Add
                  </CommonButton>
                </Form>
              </FormikProvider>
            )}
          </div>
        </div>
        <div className={styles["modal__controls-wrapper"]}>
          <PrimaryButton type="button" onClick={onClose} textSize={14}>
            Done
          </PrimaryButton>
        </div>
      </div>
    </ModalWrapper>
  );
};
export default ProviderManagerModal;
